import { graphql, type GraphQLTaggedNode } from 'react-relay';
import memoizeOne, { type MemoizedFn } from 'memoize-one';

import { readFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useReadFragmentInterop.ts';
import { getCloudId } from '@confluence/session-data/entry-points/SessionData.ts';

import type { getCachedPageDataContentFragment$data } from './__generated__/getCachedPageDataContentFragment.graphql.ts';
import type { getCachedPageDataSubTypeContentFragment$data } from './__generated__/getCachedPageDataSubTypeContentFragment.graphql.ts';
import type { getCachedPageDataSubTypePageTreeFragment$data } from './__generated__/getCachedPageDataSubTypePageTreeFragment.graphql.ts';
import type { getCachedPageDataSubTypeSearchFragment$data } from './__generated__/getCachedPageDataSubTypeSearchFragment.graphql.ts';

export type CachedPageData = {
	subType: string | undefined; // undefined if FG disabled, no contentId, or not found in fragmentData
	isArchived: boolean | undefined; // undefined if FG disabled, no contentId, or not found in fragmentData
	gqlType: string | undefined; // undefined if FG disabled, or no contentId
};

enum Client {
	CC_GRAPHQL = 'Cc graphql client',
	AGG_GRAPHQL = 'Agg graphql client',
}
const FRAGMENTS_BY_TYPE: {
	client: Client;
	gqlType: string;
	fragment: GraphQLTaggedNode;
}[] = [
	{
		client: Client.CC_GRAPHQL,
		gqlType: 'Content',
		fragment: graphql`
			fragment getCachedPageDataContentFragment on Content {
				id
				subType
				status
			}
		`,
	},
	{
		client: Client.CC_GRAPHQL,
		gqlType: 'Content',
		// If we don't have `status` cached but do have `subType` cached, this ensures we still fast load it. We prefer fast loading live docs even if we're uncertain whether they're archived.
		fragment: graphql`
			fragment getCachedPageDataSubTypeContentFragment on Content {
				id
				subType
			}
		`,
	},
	{
		client: Client.CC_GRAPHQL,
		gqlType: 'PTPage',
		fragment: graphql`
			fragment getCachedPageDataSubTypePageTreeFragment on PTPage {
				id
				subType
			}
		`,
	},
	{
		// Search query uses agg graphql client
		client: Client.AGG_GRAPHQL,
		gqlType: 'SearchConfluencePageBlogAttachment',
		fragment: graphql`
			fragment getCachedPageDataSubTypeSearchFragment on SearchConfluencePageBlogAttachment {
				id
				confluenceEntity {
					... on ConfluencePage {
						subtype
					}
				}
			}
		`,
	},
];

/* Retrieves subtype and status by contentId from Apollo cache by looking at types returned by multiple queries.
 * Why each GQL type is used:
 * PTPage is used by: Pages retrieved by Page Tree
 * Content is used by: Current page, pages retrieved by Recents (Worked on/Created by me/etc), and pages retrieved by Home's Discover feed (Following/Popular/etc)
 * NOTE: This function shouldn't need to be invoked unless the tenant has the live pages feature enable. It's recommended to make that check before invoking this function.
 */
export const getCachedPageData: MemoizedFn<(contentId?: string) => CachedPageData> = memoizeOne(
	(contentId?: string) => {
		if (!contentId) {
			// When content id does not exist, return undefined values
			return { subType: undefined, isArchived: undefined, gqlType: undefined };
		}
		const cloudId = getCloudId();

		let subType: string | undefined;
		let isArchived: boolean | undefined = undefined;
		let gqlTypeUsed: string | undefined;
		FRAGMENTS_BY_TYPE.some(({ client, gqlType, fragment }) => {
			// readFragment throws an error if it finds the item in the cache without both id and subtype, so we wrap in try/catch
			try {
				let id = `${gqlType}:${contentId}`;
				if (gqlType === 'SearchConfluencePageBlogAttachment') {
					// Search graphql query includes the ARI in the id
					id = `${gqlType}:ari:cloud:confluence:${cloudId}:page/${contentId}`;
				}
				const fragmentData = readFragmentInterop<
					| getCachedPageDataContentFragment$data
					| getCachedPageDataSubTypeContentFragment$data
					| getCachedPageDataSubTypePageTreeFragment$data
					| getCachedPageDataSubTypeSearchFragment$data
				>({
					id,
					fragment,
					interopApolloAggClient: client === Client.AGG_GRAPHQL,
				});

				if (fragmentData && 'subType' in fragmentData && fragmentData.subType) {
					subType = fragmentData.subType;
					isArchived =
						'status' in fragmentData && fragmentData.status
							? fragmentData.status?.toLowerCase?.() === 'archived'
							: undefined;
					gqlTypeUsed = gqlType;
					return true;
				} else if (fragmentData && 'subtype' in fragmentData && fragmentData.subtype) {
					// In search graphql query's response, subtype exists as an inline fragment on ConfluencePage
					subType = (fragmentData.subtype as string)?.toLowerCase?.();
					gqlTypeUsed = gqlType;
					return true;
				}
			} catch {}
		});

		return { subType, isArchived, gqlType: gqlTypeUsed };
	},
);

/**
 * Clears memoization cache on getCachedPageData function
 *
 */
export const clearMemoizedCachedPageData = (): void => getCachedPageData.clear();
